import React, { type AllHTMLAttributes, type PropsWithChildren } from 'react';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';

import stylesheet from './StackLayout.less';

// Define IDS spacing tokens
export type SpacingToken =
  | 'space-0' // 0px
  | 'space-2' // 2px
  | 'space-4' // 4px
  | 'space-6' // 6px
  | 'space-8' // 8px
  | 'space-10' // 10px
  | 'space-12' // 12px
  | 'space-16' // 16px
  | 'space-20' // 20px
  | 'space-24' // 24px
  | 'space-32' // 32px
  | 'space-40' // 40px
  | 'space-60'; // 60px

// Define numeric gap type for backward compatibility
type NumericGap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

export type StackLayoutProps = PropsWithChildren<{
  /** The node used for the root element of StackLayout. */
  as?: React.ElementType;
  /** Controls spacing between children. Uses component spacing. */
  gap?: NumericGap | SpacingToken;
}> &
  AllHTMLAttributes<unknown>;
//We are not sure what value of `as` will be passed in here,
//so I decided to use AllHTMLAttributes<unknown> here.

/**
 * `StackLayout` stacks **block-level** elements.
 *
 * All children are stacked in a single column. Inline elements get stretched
 * and become blocks. If you wish to avoid this, wrap inline elements with
 * another element, like a `<div>`.
 */
const StackLayout = React.forwardRef<HTMLElement, StackLayoutProps>(
  function StackLayout(
    { as: Component = 'div', children, className, gap = 4, ...props },
    forwardedRef,
  ) {
    const isSpacingFlagEnabled = mcdsFlagCheck(
      'xp_mcds_redesign_spacing_components',
    );

    // Add deprecation warning for numeric gap values
    if (typeof gap === 'number') {
      console.warn(
        'The numeric gap prop is deprecated. Please use spacing tokens instead (e.g. "space-2", "space-4", etc.). ' +
          'Numeric values will be removed in a future version.',
      );
    }

    // Implements the `StackLayout` on every-layout.dev:
    // https://every-layout.dev/layouts/stack
    //
    // Note that there is a bug in every-layout's version, which uses CSS
    // variables: Nested `StackLayout`s override the CSS variable of its parent,
    // causing unwanted `gap` values.
    return (
      <Component
        className={cx(
          stylesheet.stack,
          typeof gap === 'number'
            ? stylesheet[`spacing${gap}`]
            : isSpacingFlagEnabled && stylesheet[gap],
          className,
        )}
        ref={forwardedRef}
        {...props}
      >
        {children}
      </Component>
    );
  },
);

export default StackLayout;
